import axios from "axios";

const rootDomain = window.location.host;

let partnerDomain = "https://partner-travel-jab.local/";

if (window.location.hostname === "traveljab.co.uk" || window.location.hostname === "www.traveljab.co.uk") {
  partnerDomain = "https://partner.traveljab.co.uk";
}

if (window.location.hostname === "test.traveljab.co.uk") {
  partnerDomain = "https://test.partner.traveljab.co.uk";
}

const wpApi = axios.create({
  baseURL: `https://${rootDomain}/wp-json/wp/v2`,
});

const customApi = axios.create({
  baseURL: `https://${rootDomain}/wp-json/custom/v1`,
});

const partnerApi = axios.create({
  baseURL: `${partnerDomain}/wp-json/custom/v1`,
});

// If logged in, add Authentication Header to the API call
const setBearer = () => {
  const token = localStorage.getItem("userToken");

  if (token) {
    // console.log("Bearer is being set");
    const addTokenHeader = (config) => {
      config.headers.Authorization = token ? `Bearer ${token}` : "";
      return config;
    };

    wpApi.interceptors.request.use(addTokenHeader);
    customApi.interceptors.request.use(addTokenHeader);
  }
};

// Format numbers to 2 decimal places
// 16.928 should be 16.93
const formatPrice = (value) => {
  value = value || 0;
  return parseFloat(value).toFixed(2);
};

const getVaccineCategory = (obj) => {
  const properties = ["adult", "speciality", "paediatric", "seasonal", "teen"];
  const group = properties.find((key) => obj[key] === "1");
  return group;
};

// Format phone number to E.164 format
function formatToE164(phoneNumber) {
  // Remove all spaces, dashes, parentheses and other non-numeric characters
  let cleaned = phoneNumber.replace(/[\s()-]/g, "");

  // Check if the number starts with '0', indicating it might be a local UK number
  if (cleaned.startsWith("0")) {
    // Replace leading '0' with '+44'
    cleaned = "+44" + cleaned.substring(1);
  } else if (!cleaned.startsWith("+44")) {
    // If the number doesn't already start with '+44', assume it needs the UK country code
    cleaned = "+44" + cleaned;
  }

  return cleaned;
}

function formatDate(isoDateString, formatString) {
  const date = new Date(isoDateString);
  const map = {
    yyyy: date.getFullYear(),
    mm: String(date.getMonth() + 1).padStart(2, "0"), // Months are zero-indexed
    dd: String(date.getDate()).padStart(2, "0"),
  };

  return formatString.replace(/yyyy|mm|dd/g, (matched) => map[matched]);
}

function unCamelCase(camelCaseStr) {
  // Insert a space before each uppercase letter, trim any leading/trailing spaces,
  // and convert the string to title case.
  return camelCaseStr
    .replace(/([a-z])([A-Z])/g, "$1 $2") // Insert space between lowercase and uppercase
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2") // Handle multiple uppercase letters
    .replace(/(^\w|\s\w)/g, (m) => m.toUpperCase()) // Convert to title case
    .trim(); // Remove any leading or trailing spaces
}

/**
 * Serializes an object into URL query parameters.
 * @param {Object} obj - The object to be serialized into a query string.
 * @returns {Object} - An object with encoded key-value pairs.
 */
const serializeQueryParams = (obj) => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    // Check if the value is an object or array that needs to be serialized
    if (typeof value === "object" && value !== null) {
      acc[key] = encodeURIComponent(JSON.stringify(value));
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});
};

/**
 * Parses a query string from a URL and returns an object representation.
 * @param {string} url - The URL containing the query string to parse.
 * @returns {Object} - An object containing decoded key-value pairs.
 */
function parseQueryString(url) {
  const queryString = url.split("?")[1];
  const params = new URLSearchParams(queryString);
  const queryObject = {};

  for (const [key, value] of params.entries()) {
    // Decode and parse each value
    try {
      queryObject[key] = JSON.parse(decodeURIComponent(value));
    } catch (e) {
      queryObject[key] = decodeURIComponent(value); // Fallback for non-JSON values
    }
  }

  return queryObject;
}

function isEmptyObject(obj) {
  if (obj === null || obj === undefined || typeof obj !== "object") {
    return true; // or false, depending on how you want to treat non-objects
  }
  return Object.keys(obj).length === 0;
}

const applyDiscount = (price, maxDiscount) => {
  const discountPercentage = maxDiscount || 0;
  const discountAmount = (price * discountPercentage) / 100;
  return price - discountAmount;
};

const calculatePatientFees = (price) => {
  let fee = Number(price) * 0.1;
  fee = fee * 1.2;
  return fee;
};

const calculateClinicFees = (price) => {
  let fee = Number(price) * 0.1;
  let vat = fee * 0.2;
  let cardFee = Number(price) * 0.02;
  return fee + vat + cardFee;
};

// Calculate the total price for vaccinations in a clinic
// Does not apply any fees or discounts

const calculateTotalPrice = (clinic) => {
  const adultVaccinations = clinic.basket.vaccinations.adult;
  const childVaccinations = clinic.basket.vaccinations.child;

  const totalAdultPrice = adultVaccinations.flat().reduce((total, vacc) => {
    const priceMultiplier = vacc.course === "full" ? Number(vacc.dose) : 1;
    const price = Number(vacc.priceBeforeFees) * priceMultiplier;
    return total + price;
  }, 0);

  const totalChildrenPrice = childVaccinations.flat().reduce((total, vacc) => {
    const priceMultiplier = vacc.course === "full" ? Number(vacc.dose) : 1;
    const price = Number(vacc.priceBeforeFees) * priceMultiplier;
    return total + price;
  }, 0);

  return totalAdultPrice + totalChildrenPrice;
};

// Calculate the final price for a clinic, including any fees or discounts
const calculateFinalPrice = (clinic) => {
  const adultVaccinations = clinic.basket.vaccinations.adult;
  const childVaccinations = clinic.basket.vaccinations.child;
  const maxDiscount = clinic.booster_max_discount;

  const totalAdultPrice = adultVaccinations.flat().reduce((total, vacc) => {
    const priceMultiplier = vacc.course === "full" ? Number(vacc.dose) : 1;
    const price = Number(vacc.price) * priceMultiplier;

    const finalPrice =
      vacc.course === "full" ? applyDiscount(price, maxDiscount) : price;
    vacc.finalPrice = price;
    return total + finalPrice;
  }, 0);

  const totalChildrenPrice = childVaccinations.flat().reduce((total, vacc) => {
    const priceMultiplier = vacc.course === "full" ? Number(vacc.dose) : 1;
    const price = Number(vacc.price) * priceMultiplier;

    const finalPrice =
      vacc.course === "full" ? applyDiscount(price, maxDiscount) : price;
    vacc.finalPrice = price;
    return total + finalPrice;
  }, 0);

  return totalAdultPrice + totalChildrenPrice;
};

// Consultation Pricing Logic
// const PATIENT_TYPE_DETAILS = {
//   adult65: { serviceId: "1", label: "Adult 65+" },
//   adult16: { serviceId: "2", label: "Adult 16-64" },
//   youngAdult: { serviceId: "3", label: "Young Adult (12-15)" },
//   children: { serviceId: "4", label: "Child (2-11)" },
//   infant: { serviceId: "5", label: "Infant (0-1)" },
// };

function calculateFee(priceString) {
  const price = Number(priceString);
  if (isNaN(price) || price <= 0) return 0; // Ensure price is positive
  const baseFee = price * 0.1;
  return baseFee * 1.2; // Assuming VAT or similar markup
}

function calculatePatientPays(priceString) {
  const price = Number(priceString);
  if (isNaN(price) || price <= 0) return 0; // Ensure price is positive
  const fee = calculateFee(priceString); // calculateFee handles its own parsing and validation
  return price + fee;
}

function getConsultationPriceBreakdown(clinicConsultations, patientsQuery) {
  // Ensure clinicConsultations is an array and patientsQuery is an object.
  // If clinicConsultations is empty, prices can't be found.
  if (
    !Array.isArray(clinicConsultations) ||
    clinicConsultations.length === 0 ||
    !patientsQuery ||
    typeof patientsQuery !== "object"
  ) {
    return { totalPrice: 0, breakdown: [] };
  }

  let grandTotalPrice = 0;
  const patientTypeBreakdown = [];

  for (const patientTypeKey in patientsQuery) {
    const patientInfo = patientsQuery[patientTypeKey]; // e.g., patientsQuery.adult65

    // Skip entries that are not valid patient type objects (e.g., "total", "totalAdults")
    // or lack necessary information like 'id' or 'count'.
    if (
      typeof patientInfo !== "object" ||
      patientInfo === null ||
      typeof patientInfo.id === "undefined" || // This 'id' is the service_id
      typeof patientInfo.count !== "number"
    ) {
      continue;
    }

    const count = patientInfo.count;

    if (count > 0) {
      // Find the corresponding service in clinicConsultations using patientInfo.id.
      // clinicConsultations.service_id is a string, patientInfo.id might be a number or string.
      // Ensure consistent comparison by converting both to strings.
      const serviceIdToFind = String(patientInfo.id);
      const consultationService = clinicConsultations.find(
        (c) => String(c.service_id) === serviceIdToFind
      );

      if (consultationService) {
        const basePatientPrice = consultationService.patient_price; // e.g., "34.00"
        const baseSubsequentPrice = consultationService.subsequent_price; // e.g., "20.00"

        // calculatePatientPays handles string-to-number conversion and fee calculation
        const actualPatientPrice = calculatePatientPays(basePatientPrice);
        const actualSubsequentPrice = calculatePatientPays(baseSubsequentPrice);

        const calculatedPricesForType = [];
        let totalForThisType = 0;

        // First patient of this type
        if (count >= 1 && actualPatientPrice > 0) {
          calculatedPricesForType.push(actualPatientPrice);
          totalForThisType += actualPatientPrice;
        }

        // Subsequent patients of this type
        for (let i = 1; i < count; i++) {
          const priceToAdd =
            actualSubsequentPrice > 0 // Use subsequent price if available and positive
              ? actualSubsequentPrice
              : actualPatientPrice; // Fallback to the first patient's price
          if (priceToAdd > 0) {
            calculatedPricesForType.push(priceToAdd);
            totalForThisType += priceToAdd;
          }
        }

        grandTotalPrice += totalForThisType;
        if (totalForThisType > 0 || calculatedPricesForType.length > 0) {
          patientTypeBreakdown.push({
            patientType: patientTypeKey, // e.g., "adult65"
            label: patientInfo.name || patientTypeKey, // Use name from patientInfo, fallback to key
            count: count,
            serviceId: patientInfo.id, // The service_id from patientsQuery
            calculatedPrices: calculatedPricesForType,
            totalForType: totalForThisType,
          });
        }
      }
      // If no matching consultationService is found, this patient type won't contribute to the price.
      // You could add a console.warn here if needed for debugging.
    }
  }

  return { totalPrice: grandTotalPrice, breakdown: patientTypeBreakdown };
}

function obscureNumber(number) {
  if (typeof number !== "string" || number.length < 3) {
    throw new Error("Invalid mobile number input");
  }
  const lastThreeDigits = number.slice(-3);
  const obscuredPart = "*".repeat(number.length - 3);
  return `${obscuredPart}${lastThreeDigits}`;
}

// Helper to get the base URL for custom API endpoints
const getApiBaseUrl = () => {
  return window.wpApiSettings?.root || "/wp-json"; // Use localized root, fallback if not set
};

// Helper to get the site's base URL
const getSiteUrl = () => {
  return window.wpApiSettings?.site_url || window.location.origin; // Use localized site_url, fallback to origin
};

/**
 * Splits a full name into first name and last name components
 * @param {string} fullName - The full name to split
 * @returns {Object} - Object containing firstName and lastName
 */
const splitFullName = (fullName) => {
  // Handle null, undefined, or non-string inputs
  if (!fullName || typeof fullName !== "string") {
    return { firstName: "", lastName: "" };
  }

  // Trim whitespace and normalize spaces
  const trimmedName = fullName.trim().replace(/\s+/g, " ");

  // Handle empty string after trimming
  if (!trimmedName) {
    return { firstName: "", lastName: "" };
  }

  // Split by spaces
  const nameParts = trimmedName.split(" ");

  // Handle single name (use as firstName, empty lastName)
  if (nameParts.length === 1) {
    return { firstName: nameParts[0], lastName: "" };
  }

  // Handle multiple parts: first part is firstName, rest joined as lastName
  const firstName = nameParts[0];
  const lastName = nameParts.slice(1).join(" ");

  return { firstName, lastName };
};

/**
 * Convert time string (HH:MM) to minutes from midnight
 * @param {string} timeString - Time in format "HH:MM"
 * @returns {number} - Minutes from midnight
 */
const timeToMinutes = (timeString) => {
  if (!timeString || typeof timeString !== "string") return 0;
  const [hours, minutes] = timeString.split(":").map(Number);
  return hours * 60 + (minutes || 0);
};

/**
 * Get day of week name from date
 * @param {Date|string} date - Date object or date string
 * @returns {string} - Day name in lowercase (monday, tuesday, etc.)
 */
const getDayOfWeek = (date) => {
  if (!date) return "";
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const days = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  return days[dateObj.getDay()];
};

/**
 * Check if a time is within a given range
 * @param {string} time - Time to check (HH:MM)
 * @param {string} openTime - Opening time (HH:MM)
 * @param {string} closeTime - Closing time (HH:MM)
 * @returns {boolean} - True if time is within range
 */
const isTimeInRange = (time, openTime, closeTime) => {
  if (!time || !openTime || !closeTime) return false;

  const timeMinutes = timeToMinutes(time);
  const openMinutes = timeToMinutes(openTime);
  const closeMinutes = timeToMinutes(closeTime);

  return timeMinutes >= openMinutes && timeMinutes <= closeMinutes;
};

/**
 * Filter available time slots based on clinic opening times
 * @param {Array} allSlots - All possible time slots
 * @param {Object} openingTimes - Clinic opening times object
 * @param {Date|string} selectedDate - Selected appointment date
 * @returns {Array} - Filtered time slots
 */
const filterAvailableSlots = (allSlots, openingTimes, selectedDate) => {
  if (!allSlots || !Array.isArray(allSlots) || !selectedDate) {
    return allSlots || [];
  }

  const dayName = getDayOfWeek(selectedDate);
  const daySchedule = openingTimes[dayName];

  // If no schedule for this day or clinic is closed
  if (!daySchedule || !daySchedule.is_open) {
    return [];
  }

  return allSlots.filter((slot) => {
    // Check first time period
    const inFirstPeriod =
      daySchedule.opening_time_1 &&
      daySchedule.closing_time_1 &&
      isTimeInRange(
        slot,
        daySchedule.opening_time_1,
        daySchedule.closing_time_1
      );

    // Check second time period (if exists)
    const inSecondPeriod =
      daySchedule.opening_time_2 &&
      daySchedule.closing_time_2 &&
      isTimeInRange(
        slot,
        daySchedule.opening_time_2,
        daySchedule.closing_time_2
      );

    return inFirstPeriod || inSecondPeriod;
  });
};

/**
 * Check if a clinic is open on a specific date
 * @param {Date} date - Date to check
 * @param {Object} openingTimes - Clinic opening times object
 * @returns {boolean} - True if clinic is open on this date
 */
const isClinicOpenOnDate = (date, openingTimes) => {
  if (!date || !openingTimes) return true; // Default to open if no data

  const dayName = getDayOfWeek(date);
  const daySchedule = openingTimes[dayName];

  // If no schedule for this day or clinic is closed
  return daySchedule && daySchedule.is_open;
};

/**
 * Get disabled weekday numbers based on clinic opening times
 * @param {Object} openingTimes - Clinic opening times object
 * @returns {Array} - Array of disabled weekday numbers (0=Sunday, 1=Monday, etc.)
 */
const getDisabledWeekdays = (openingTimes) => {
  if (!openingTimes || Object.keys(openingTimes).length === 0) {
    return []; // No restrictions if no opening times
  }

  const dayMapping = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  const disabledDays = [];

  Object.keys(dayMapping).forEach((dayName) => {
    const daySchedule = openingTimes[dayName];
    // If no schedule for this day or clinic is closed, disable it
    if (!daySchedule || !daySchedule.is_open) {
      disabledDays.push(dayMapping[dayName]);
    }
  });

  return disabledDays;
};

/**
 * Parse unavailable days JSON string
 * @param {string} unavailableDaysString - JSON string of unavailable days
 * @returns {Array} - Parsed array of unavailable day objects
 */
const parseUnavailableDays = (unavailableDaysString) => {
  if (!unavailableDaysString || typeof unavailableDaysString !== "string") {
    return [];
  }

  try {
    const parsed = JSON.parse(unavailableDaysString);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error("Error parsing unavailable days:", error);
    return [];
  }
};

/**
 * Parse date string in DD/MM/YYYY format to Date object
 * @param {string} dateString - Date in DD/MM/YYYY format
 * @returns {Date|null} - Date object or null if invalid
 */
const parseDateString = (dateString) => {
  if (!dateString || typeof dateString !== "string") return null;

  const parts = dateString.split("/");
  if (parts.length !== 3) return null;

  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
  const year = parseInt(parts[2], 10);

  const date = new Date(year, month, day);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Check if a date falls within unavailable days
 * @param {Date} date - Date to check
 * @param {Array} unavailableDays - Array of unavailable day objects
 * @returns {Object|null} - Unavailable day object if date is unavailable, null otherwise
 */
const getUnavailableDayForDate = (date, unavailableDays) => {
  if (!date || !Array.isArray(unavailableDays)) return null;

  return (
    unavailableDays.find((unavailableDay) => {
      const fromDate = parseDateString(unavailableDay.dateFrom);
      const toDate = parseDateString(unavailableDay.dateTo);

      if (!fromDate || !toDate) return false;

      // For date-only comparison, we need to check if the date falls within the range
      // But we need to consider the times as well for precise unavailability
      const dateOnly = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      );
      const fromDateOnly = new Date(
        fromDate.getFullYear(),
        fromDate.getMonth(),
        fromDate.getDate()
      );
      const toDateOnly = new Date(
        toDate.getFullYear(),
        toDate.getMonth(),
        toDate.getDate()
      );

      return dateOnly >= fromDateOnly && dateOnly <= toDateOnly;
    }) || null
  );
};

/**
 * Get disabled dates based on unavailable days
 * @param {Array} unavailableDays - Array of unavailable day objects
 * @param {Date} startDate - Start date for generating disabled dates
 * @param {number} daysAhead - Number of days ahead to generate (default: 365)
 * @returns {Array} - Array of disabled Date objects
 */
const getDisabledDatesFromUnavailable = (
  unavailableDays,
  startDate = new Date(),
  daysAhead = 365
) => {
  if (!Array.isArray(unavailableDays) || unavailableDays.length === 0) {
    return [];
  }

  const disabledDates = [];
  const currentDate = new Date(startDate);

  for (let i = 0; i < daysAhead; i++) {
    const checkDate = new Date(currentDate);
    checkDate.setDate(currentDate.getDate() + i);

    const unavailableDay = getUnavailableDayForDate(checkDate, unavailableDays);

    if (unavailableDay) {
      // If times is empty, the clinic is completely closed for the entire date range
      if (!unavailableDay.times || unavailableDay.times.trim() === "") {
        disabledDates.push(new Date(checkDate));
      } else {
        // Get the date boundaries
        const fromDate = parseDateString(unavailableDay.dateFrom);
        const toDate = parseDateString(unavailableDay.dateTo);

        if (fromDate && toDate) {
          const dateOnly = new Date(
            checkDate.getFullYear(),
            checkDate.getMonth(),
            checkDate.getDate()
          );
          const fromDateOnly = new Date(
            fromDate.getFullYear(),
            fromDate.getMonth(),
            fromDate.getDate()
          );
          const toDateOnly = new Date(
            toDate.getFullYear(),
            toDate.getMonth(),
            toDate.getDate()
          );

          // Disable middle dates (completely unavailable)
          if (
            dateOnly.getTime() > fromDateOnly.getTime() &&
            dateOnly.getTime() < toDateOnly.getTime()
          ) {
            disabledDates.push(new Date(checkDate));
          }
          // Keep first and last dates enabled - they will have partial availability handled by time picker
        }
      }
    }
  }

  return disabledDates;
};

/**
 * Filter time slots considering both opening times and unavailable days
 * @param {Array} allSlots - All possible time slots
 * @param {Object} openingTimes - Clinic opening times object
 * @param {Date|string} selectedDate - Selected appointment date
 * @param {Array} unavailableDays - Array of unavailable day objects
 * @returns {Array} - Filtered time slots
 */
const filterAvailableSlotsWithUnavailable = (
  allSlots,
  openingTimes,
  selectedDate,
  unavailableDays = []
) => {
  if (!allSlots || !Array.isArray(allSlots) || !selectedDate) {
    return allSlots || [];
  }

  // First filter by regular opening times
  const regularlyAvailableSlots = filterAvailableSlots(
    allSlots,
    openingTimes,
    selectedDate
  );

  // If no unavailable days, return regular filtering
  if (!Array.isArray(unavailableDays) || unavailableDays.length === 0) {
    return regularlyAvailableSlots;
  }

  const dateObj =
    typeof selectedDate === "string" ? new Date(selectedDate) : selectedDate;
  const unavailableDay = getUnavailableDayForDate(dateObj, unavailableDays);

  // If date is not in unavailable days, return regular filtering
  if (!unavailableDay) {
    return regularlyAvailableSlots;
  }

  // If date is unavailable with no times specified, return empty (completely closed)
  if (!unavailableDay.times || unavailableDay.times.trim() === "") {
    return [];
  }

  // Parse the time range from unavailable day
  const timeRange = unavailableDay.times.trim();
  const timeRangeParts = timeRange.split(" - ");

  if (timeRangeParts.length !== 2) {
    return []; // Invalid time format, assume closed
  }

  const [timeStart, timeEnd] = timeRangeParts;

  // Get the date boundaries
  const fromDate = parseDateString(unavailableDay.dateFrom);
  const toDate = parseDateString(unavailableDay.dateTo);

  if (!fromDate || !toDate) {
    return []; // Invalid dates, assume closed
  }

  const dateOnly = new Date(
    dateObj.getFullYear(),
    dateObj.getMonth(),
    dateObj.getDate()
  );
  const fromDateOnly = new Date(
    fromDate.getFullYear(),
    fromDate.getMonth(),
    fromDate.getDate()
  );
  const toDateOnly = new Date(
    toDate.getFullYear(),
    toDate.getMonth(),
    toDate.getDate()
  );

  // Determine availability based on position within the unavailable period
  if (
    dateOnly.getTime() === fromDateOnly.getTime() &&
    dateOnly.getTime() === toDateOnly.getTime()
  ) {
    // Single day period: unavailable during the specified time range
    return regularlyAvailableSlots.filter((slot) => {
      return !isTimeInRange(slot, timeStart, timeEnd);
    });
  } else if (dateOnly.getTime() === fromDateOnly.getTime()) {
    // First day of period: unavailable from timeStart onwards
    return regularlyAvailableSlots.filter((slot) => {
      return timeToMinutes(slot) < timeToMinutes(timeStart);
    });
  } else if (dateOnly.getTime() === toDateOnly.getTime()) {
    // Last day of period: available from timeEnd onwards
    return regularlyAvailableSlots.filter((slot) => {
      return timeToMinutes(slot) >= timeToMinutes(timeEnd);
    });
  } else {
    // Middle days of period: completely unavailable
    return [];
  }
};

export {
  wpApi,
  customApi,
  partnerApi,
  unCamelCase,
  formatPrice,
  formatDate,
  serializeQueryParams,
  parseQueryString,
  setBearer,
  getVaccineCategory,
  formatToE164,
  isEmptyObject,
  calculateTotalPrice,
  calculateFinalPrice,
  calculateClinicFees,
  calculatePatientFees,
  applyDiscount,
  getApiBaseUrl,
  getSiteUrl,
  obscureNumber,
  getConsultationPriceBreakdown,
  splitFullName,
  // Time filtering helpers
  timeToMinutes,
  getDayOfWeek,
  isTimeInRange,
  filterAvailableSlots,
  // Date filtering helpers
  isClinicOpenOnDate,
  getDisabledWeekdays,
  // Unavailable days helpers
  parseUnavailableDays,
  parseDateString,
  getUnavailableDayForDate,
  getDisabledDatesFromUnavailable,
  filterAvailableSlotsWithUnavailable,
};
